import { IconBuildingBank, IconCheck, IconFileInvoice, IconListCheck, IconShieldCheck, type IconProps } from "@tabler/icons-react";
import { useEffect, useState, type ComponentType } from "react";
import { Card, Group, Loader, Stack, Text } from "@/components/ui";

interface Paso {
  icon: ComponentType<IconProps>;
  label: string;
  desc: string;
}

const PASOS: Paso[] = [
  { icon: IconListCheck, label: "Validando reglas del CFDI", desc: "RN1–RN10 · datos, moneda y consistencia" },
  { icon: IconBuildingBank, label: "Consultando al SAT", desc: "Servicio de estatus de comprobantes en línea" },
  { icon: IconShieldCheck, label: "Verificando vigencia y EFOS", desc: "Confirmando que el comprobante es válido" },
];

// Animaciones scoped por prefijo `satv-` para no colisionar con otros estilos.
const CSS = `
@keyframes satv-spin { to { transform: rotate(360deg); } }
@keyframes satv-pulse {
  0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-multiva-600) 45%, transparent); }
  50% { box-shadow: 0 0 0 12px color-mix(in srgb, var(--color-multiva-600) 0%, transparent); }
}
@keyframes satv-slide {
  0% { left: -45%; }
  100% { left: 100%; }
}
@keyframes satv-fadein {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
.satv-orb { position: relative; width: 54px; height: 54px; flex-shrink: 0; }
.satv-orb-ring {
  position: absolute; inset: 0; border-radius: 50%;
  border: 3px solid color-mix(in srgb, var(--color-multiva-600) 22%, transparent);
  border-top-color: var(--color-multiva-600);
  animation: satv-spin 0.9s linear infinite;
}
.satv-orb-core {
  position: absolute; inset: 8px; border-radius: 50%; color: white;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, var(--color-multiva-600), var(--color-multiva-700));
  animation: satv-pulse 1.8s ease-out infinite;
}
.satv-track {
  position: relative; height: 4px; border-radius: 2px; overflow: hidden;
  background: color-mix(in srgb, var(--color-multiva-600) 15%, transparent);
}
.satv-fill {
  position: absolute; top: 0; height: 100%; width: 45%; border-radius: 2px;
  background: linear-gradient(90deg, transparent, var(--color-multiva-600), transparent);
  animation: satv-slide 1.15s ease-in-out infinite;
}
.satv-step-icon {
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: background 250ms ease, color 250ms ease;
}
`;

/**
 * Panel animado que se muestra mientras el CFDI está en proceso de validación
 * (reglas + consulta al SAT). Da feedback visual continuo: un orbe giratorio
 * con pulso, una barra de progreso indeterminada y una secuencia de 3 pasos
 * que avanza (check en los completados, spinner en el activo). Los pasos
 * avanzan solos y se quedan en el último mientras siga la espera · no implica
 * un porcentaje real (la duración del SAT es variable).
 */
export function SatValidacionProgreso() {
  const [activo, setActivo] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActivo((a) => (a < PASOS.length - 1 ? a + 1 : a)), 1500);
    return () => clearInterval(t);
  }, []);

  return (
    <Card
      padding="lg"
      style={{
        borderColor: "color-mix(in srgb, var(--color-multiva-600) 35%, transparent)",
        background: "color-mix(in srgb, var(--color-multiva-600) 5%, var(--color-card))",
        animation: "satv-fadein 240ms ease",
      }}
    >
      <style>{CSS}</style>
      <Stack gap="md">
        <Group gap="md" wrap="nowrap" align="center">
          <div className="satv-orb">
            <div className="satv-orb-ring" />
            <div className="satv-orb-core">
              <IconFileInvoice size={22} stroke={1.7} />
            </div>
          </div>
          <Stack gap={2} className="min-w-0 flex-1">
            <Text fw={700} size="sm">
              Validando el CFDI…
            </Text>
            <Text size="xs" c="dimmed">
              Estamos consultando el SAT · esto puede tardar unos segundos, no cierres esta pantalla.
            </Text>
          </Stack>
        </Group>

        <div className="satv-track">
          <div className="satv-fill" />
        </div>

        <Stack gap="xs">
          {PASOS.map((p, idx) => {
            const completado = idx < activo;
            const enCurso = idx === activo;
            const Icon = p.icon;
            const bg = completado
              ? "color-mix(in srgb, var(--color-exito-600) 15%, transparent)"
              : enCurso
                ? "color-mix(in srgb, var(--color-multiva-600) 15%, transparent)"
                : "var(--color-muted)";
            const color = completado ? "var(--color-exito-600)" : enCurso ? "var(--color-multiva-600)" : "var(--color-muted-foreground)";
            return (
              <Group key={p.label} gap="sm" wrap="nowrap" style={{ opacity: completado || enCurso ? 1 : 0.5 }}>
                <div className="satv-step-icon" style={{ background: bg, color }}>
                  {completado ? <IconCheck size={16} stroke={2.2} /> : enCurso ? <Loader size="sm" color="multiva" /> : <Icon size={16} stroke={1.7} />}
                </div>
                <Stack gap={0} className="min-w-0">
                  <Text size="sm" fw={enCurso ? 600 : 500}>
                    {p.label}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {p.desc}
                  </Text>
                </Stack>
              </Group>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
}
